import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { userAPI, dashboardAPI, paymentsAPI } from '../api/api';
import { LANGUAGES as TRANSLATE_LANGUAGES } from '../services/translationService';
import AdminViewBanner from '../components/AdminViewBanner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { getPlanDisplayName, getPlanFeatures, CONTRACTOR_PLANS } from '../utils/planFeatures';
import { 
  Briefcase,
  CalendarBlank, 
  Users, 
  CurrencyDollar, 
  Plus,
  FileText,
  ChatCircle,
  Bell,
  Gear,
  User,
  ChartBar,
  ShieldCheck,
  Envelope,
  Phone,
  MapPin,
  Buildings,
  Crown,
  DownloadSimple,
  SpinnerGap,
  FloppyDisk,
  MagnifyingGlass,
  Rocket
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import TwoFactorSettings from '../components/TwoFactorSettings';
import ProToolsPanel from '../components/ProToolsPanel';
import TelegramLinkCard from '../components/TelegramLinkCard';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { AccountPrivacyCard } from '../components/AccountPrivacyCard';
import PushNotificationSettings from '../components/PushNotificationSettings';
import PlanComparisonModal from '../components/PlanComparisonModal';
import { BreadcrumbsInline } from '../components/Breadcrumbs';
import ProfileCompletenessCard from '../components/ProfileCompletenessCard';

const ContractorControlPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [userName, setUserName] = useState('');
  const [currentPlan, setCurrentPlan] = useState('starter_contractor');
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState(null);

  useEffect(() => {
    if (activeTab === 'billing' && paymentHistory === null) {
      paymentsAPI.getHistory()
        .then(({ data }) => setPaymentHistory(data.transactions || []))
        .catch(() => setPaymentHistory([]));
    }
  }, [activeTab, paymentHistory]);
  
  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState({
    open_jobs: 0,
    total_applications: 0,
    month_spending: 0,
    hired_workers: 0
  });
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    company: '',
    bio: '',
    license: '',
    insurance: '',
    verified: false
  });

  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_alerts: false,
    weekly_digest: true,
    application_updates: true,
    profile_visible: true,
    show_contact_public: true,
    allow_messages: true,
    show_availability: true
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const response = await dashboardAPI.getContractorDashboard();
      if (response.data.success) {
        setDashboardStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?type=contractor');
      return;
    }
    
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await userAPI.getMe();
        const user = response.data?.user || response.data;
        setUserName(user.name || '');
        // Set subscription plan
        if (user.subscription_plan && user.subscription_status === 'active') {
          setCurrentPlan(user.subscription_plan);
        } else {
          setCurrentPlan('starter_contractor'); // Default free plan
        }
        setProfileData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          location: user.location || '',
          company: user.company || '',
          bio: user.bio || '',
          license: user.license || '',
          insurance: user.insurance || 'Active',
          verified: !!user.verified
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };
    
    const fetchSettings = async () => {
      try {
        const response = await userAPI.getSettings();
        setSettings(response.data.settings || {});
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    fetchProfile();
    fetchDashboardStats();
    fetchSettings();
  }, [navigate, location.key, fetchDashboardStats]);

  // Handle URL tab parameter
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [searchParams]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await userAPI.updateProfile({
        name: profileData.name,
        phone: profileData.phone,
        location: profileData.location,
        company: profileData.company,
        bio: profileData.bio
      });
      setUserName(profileData.name);
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save profile.',
        variant: 'destructive'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await userAPI.updateSettings(settings);
      toast({
        title: 'Settings Saved',
        description: 'Your preferences have been updated.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings.',
        variant: 'destructive'
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleExportData = () => {
    toast({
      title: 'Export Started',
      description: 'Your data export will be ready soon.',
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'projects', label: 'My Projects', icon: Briefcase },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'billing', label: 'Billing', icon: CurrencyDollar },
    { id: 'security', label: 'Security', icon: ShieldCheck },
    { id: 'settings', label: 'Settings', icon: Gear },
  ];

  // Business Profile Completeness — 8 fields, each worth 12.5%
  const completenessFields = [
    { key: 'name', label: 'Contact name', filled: !!profileData.name?.trim() },
    { key: 'phone', label: 'Phone number', filled: !!profileData.phone?.trim() },
    { key: 'location', label: 'Location', filled: !!profileData.location?.trim() },
    { key: 'company', label: 'Company name', filled: !!profileData.company?.trim() },
    { key: 'bio', label: 'Business bio', filled: !!profileData.bio?.trim() && profileData.bio.trim().length >= 30 },
    { key: 'license', label: 'License #', filled: !!profileData.license?.trim() },
    { key: 'insurance', label: 'Insurance', filled: !!profileData.insurance?.trim() && profileData.insurance.trim().toLowerCase() !== 'inactive' },
    { key: 'verified', label: 'Verified Pro', filled: !!profileData.verified },
  ];
  const filledCount = completenessFields.filter(f => f.filled).length;
  const profileCompleteness = Math.round((filledCount / completenessFields.length) * 100);
  const missingFields = completenessFields.filter(f => !f.filled);

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="contractor-control-panel">
      <AdminViewBanner role="contractor" />
      {/* Simplified Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/contractor-dashboard' },
              { label: 'Settings' }
            ]}
            className="mb-2"
          />
          <h1 className="text-3xl md:text-4xl font-black">Settings</h1>
          <p className="text-blue-100 text-lg mt-1">Manage your account and business</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button 
            onClick={() => navigate('/post-job')}
            className="flex items-center gap-2 h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-post-job"
          >
            <Plus size={24} weight="bold" />
            Post Job
          </button>
          <button 
            onClick={() => navigate('/my-jobs')}
            className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-my-jobs"
          >
            <Briefcase size={24} weight="bold" />
            My Jobs
          </button>
          <button 
            onClick={() => navigate('/find-workers')}
            className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-find-workers"
          >
            <MagnifyingGlass size={24} weight="bold" />
            Find Workers
          </button>
        </div>

        {/* Tab Navigation - Horizontal Scroll */}
        <div className="bg-white border-2 border-black rounded-xl p-2 mb-6 overflow-x-auto shadow-[4px_4px_0px_#000]">
          <div className="flex gap-2 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-[#2B4C6F] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={24} weight="bold" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              {loadingStats ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                    <Briefcase size={32} weight="bold" className="text-[#2B4C6F] mb-3" />
                    <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Open Jobs</p>
                    <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboardStats.open_jobs}</p>
                  </div>

                  <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                    <FileText size={32} weight="bold" className="text-purple-600 mb-3" />
                    <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Applications</p>
                    <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboardStats.total_applications}</p>
                  </div>

                  <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                    <CurrencyDollar size={32} weight="bold" className="text-emerald-600 mb-3" />
                    <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Spent This Month</p>
                    <p className="text-3xl font-black text-emerald-600">${dashboardStats.month_spending?.toLocaleString() || 0}</p>
                  </div>

                  <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                    <Users size={32} weight="bold" className="text-[#2B4C6F] mb-3" />
                    <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Hired Workers</p>
                    <p className="text-3xl font-black text-neutral-900 dark:text-white">{dashboardStats.hired_workers}</p>
                  </div>
                </div>
              )}

              {/* Profile Completeness Meter */}
              <ProfileCompletenessCard
                percent={profileCompleteness}
                missingFields={missingFields}
                onComplete={() => setActiveTab('profile')}
                rewardCopy="Business profile is 100% complete — homeowners trust you first!"
              />

              {/* Account Summary */}
              <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-black text-neutral-900 dark:text-white">Account Summary</h2>
                  <button
                    onClick={() => setShowPlanComparison(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#2B4C6F] text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all text-sm"
                    data-testid="compare-plans-btn"
                  >
                    <Rocket size={16} weight="bold" />
                    {currentPlan === 'starter_contractor' ? 'Upgrade Plan' : 'Compare Plans'}
                  </button>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 ${
                      currentPlan === 'enterprise_contractor' ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400' :
                      currentPlan === 'business_contractor' ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400' :
                      'bg-neutral-100 dark:bg-neutral-700 border-neutral-400'
                    }`}>
                      <Crown size={28} weight="fill" className={
                        currentPlan === 'enterprise_contractor' ? 'text-amber-600' :
                        currentPlan === 'business_contractor' ? 'text-blue-600' :
                        'text-neutral-500'
                      } />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 font-bold">Current Plan</p>
                      <p className="text-xl font-black text-neutral-900 dark:text-white">{getPlanDisplayName(currentPlan)}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        0% commission
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border-2 border-blue-400">
                      <Briefcase size={28} weight="bold" className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 font-bold">Open Jobs</p>
                      <p className="text-xl font-black text-neutral-900 dark:text-white">{dashboardStats.open_jobs}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {getPlanFeatures('contractor', currentPlan)?.jobPostingsLimit === -1 
                          ? 'Unlimited' 
                          : `${getPlanFeatures('contractor', currentPlan)?.jobPostingsLimit || 3}/month limit`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center border-2 border-green-400">
                      <Users size={28} weight="bold" className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 font-bold">Workers Hired</p>
                      <p className="text-xl font-black text-neutral-900 dark:text-white">{dashboardStats.hired_workers}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h2 className="text-xl font-black text-gray-900 mb-4">My Projects</h2>
              <p className="text-gray-600 mb-4">Manage all your active and completed projects.</p>
              <button 
                onClick={() => navigate('/my-jobs')}
                className="h-14 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              >
                <Briefcase size={20} weight="bold" className="inline mr-2" />
                Go to My Jobs
              </button>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black text-gray-900">Your Profile</h2>
                <button
                  onClick={() => navigate('/my-availability')}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 font-bold text-sm rounded-lg border-2 border-emerald-300 hover:opacity-80 transition-all"
                  data-testid="contractor-availability-btn"
                >
                  <CalendarBlank size={16} weight="bold" /> My Availability Calendar
                </button>
              </div>
              
              {loadingProfile ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-lg font-bold mb-2 block">Full Name</Label>
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                        className="h-14 text-lg border-2 border-black rounded-xl"
                        data-testid="profile-name"
                      />
                    </div>
                    <div>
                      <Label className="text-lg font-bold mb-2 block">Email</Label>
                      <Input
                        value={profileData.email}
                        disabled
                        className="h-14 text-lg border-2 border-gray-300 rounded-xl bg-gray-50"
                        data-testid="profile-email"
                      />
                    </div>
                    <div>
                      <Label className="text-lg font-bold mb-2 block">Phone</Label>
                      <Input
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="h-14 text-lg border-2 border-black rounded-xl"
                        placeholder="(555) 123-4567"
                        data-testid="profile-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-lg font-bold mb-2 block">Location</Label>
                      <Input
                        value={profileData.location}
                        onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                        className="h-14 text-lg border-2 border-black rounded-xl"
                        placeholder="City, State"
                        data-testid="profile-location"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-lg font-bold mb-2 block">Company Name</Label>
                      <Input
                        value={profileData.company}
                        onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                        className="h-14 text-lg border-2 border-black rounded-xl"
                        placeholder="Your Construction Company LLC"
                        data-testid="profile-company"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-lg font-bold mb-2 block">About Your Business</Label>
                      <Textarea
                        value={profileData.bio}
                        onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                        className="text-lg border-2 border-black rounded-xl min-h-[120px]"
                        placeholder="Tell workers about your company..."
                        data-testid="profile-bio"
                      />
                    </div>
                  </div>

                  <button 
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="h-14 px-8 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? (
                      <><SpinnerGap size={20} weight="bold" className="inline mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><FloppyDisk size={20} weight="bold" className="inline mr-2" /> Save Profile</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">Current Plan</h2>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center border-2 ${
                    currentPlan === 'enterprise_contractor' ? 'bg-amber-100 border-amber-400' :
                    currentPlan === 'business_contractor' ? 'bg-blue-100 border-blue-400' :
                    'bg-neutral-100 border-neutral-400'
                  }`}>
                    <Crown size={32} weight="fill" className={
                      currentPlan === 'enterprise_contractor' ? 'text-amber-600' :
                      currentPlan === 'business_contractor' ? 'text-blue-600' :
                      'text-neutral-500'
                    } />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-gray-900">{getPlanDisplayName(currentPlan)} Plan</p>
                    <p className="text-gray-600">
                      {currentPlan === 'starter_contractor' ? 'Free' : 
                       currentPlan === 'business_contractor' ? '$79/month' : 
                       currentPlan === 'enterprise_contractor' ? '$199/month' : 'Free'}
                      {currentPlan !== 'starter_contractor' && ' • Renews monthly'}
                    </p>
                    <p className="text-sm text-neutral-500 mt-1">
                      0% platform commission — keep 100% of project payments
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowPlanComparison(true)}
                    className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                    data-testid="billing-compare-plans-btn"
                  >
                    <Rocket size={20} weight="bold" className="inline mr-2" />
                    {currentPlan === 'starter_contractor' ? 'Upgrade Plan' : 'Compare Plans'}
                  </button>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="h-12 px-6 bg-gray-100 text-gray-700 font-bold rounded-xl border-2 border-gray-300 hover:bg-gray-200 transition-all"
                  >
                    View All Plans
                  </button>
                </div>
              </div>

              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">Payment History</h2>
                <div className="space-y-3" data-testid="payment-history-list">
                  {paymentHistory === null ? (
                    <p className="text-gray-500 text-sm py-4 text-center">Loading payment history...</p>
                  ) : paymentHistory.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4 text-center" data-testid="payment-history-empty">No payments yet — upgrades and purchases will appear here.</p>
                  ) : (
                    paymentHistory.map((tx) => (
                      <div key={tx._id} className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl" data-testid={`payment-history-item-${tx._id}`}>
                        <div>
                          <p className="font-bold text-gray-900">
                            {tx.created_at ? new Date(tx.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </p>
                          <p className="text-sm text-gray-500">{tx.product_name || tx.product_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">${Number(tx.amount || 0).toFixed(2)}</p>
                          <span className={`text-sm font-bold ${tx.payment_status === 'paid' ? 'text-green-600' : tx.payment_status === 'pending' ? 'text-amber-600' : 'text-gray-500'}`}>
                            {tx.payment_status === 'paid' ? 'Paid' : tx.payment_status === 'pending' ? 'Pending' : (tx.payment_status || 'Unknown')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <ChangePasswordCard />
              <AccountPrivacyCard />
              <TwoFactorSettings />

              <ProToolsPanel />
              <PushNotificationSettings />
              <TelegramLinkCard />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-6">Notification Settings</h2>
                <div className="space-y-4">
                  {[
                    { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive updates via email' },
                    { key: 'sms_alerts', label: 'SMS Alerts', desc: 'Get text messages for urgent updates' },
                    { key: 'weekly_digest', label: 'Weekly Digest', desc: 'Summary of your activity' },
                    { key: 'application_updates', label: 'Application Updates', desc: 'When workers apply to your jobs' },
                  ].map((setting) => (
                    <div key={setting.key} className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl">
                      <div>
                        <p className="font-bold text-gray-900">{setting.label}</p>
                        <p className="text-sm text-gray-500">{setting.desc}</p>
                      </div>
                      <button
                        onClick={() => setSettings({...settings, [setting.key]: !settings[setting.key]})}
                        className={`w-14 h-8 rounded-full transition-all ${
                          settings[setting.key] ? 'bg-[#2B4C6F]' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-6 h-6 bg-white rounded-full shadow transition-all ${
                          settings[setting.key] ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 border-2 border-gray-200 rounded-xl">
                  <p className="font-bold text-gray-900">Preferred Language</p>
                  <p className="text-sm text-gray-500 mb-3">Chat messages translate to this language when you tap "Translate".</p>
                  <select
                    value={settings.preferred_language || 'en'}
                    onChange={(e) => setSettings({ ...settings, preferred_language: e.target.value })}
                    className="w-full sm:w-72 h-11 px-3 border-2 border-gray-300 rounded-xl font-medium text-gray-900 bg-white"
                    data-testid="preferred-language-select"
                  >
                    {Object.values(TRANSLATE_LANGUAGES).map((l) => (
                      <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                    ))}
                  </select>
                </div>

                <button 
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="mt-6 h-14 px-8 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <><SpinnerGap size={20} weight="bold" className="inline mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><FloppyDisk size={20} weight="bold" className="inline mr-2" /> Save Settings</>
                  )}
                </button>
              </div>

              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">Export Data</h2>
                <p className="text-gray-600 mb-4">Download all your account data including jobs, applications, and payment history.</p>
                <button 
                  onClick={handleExportData}
                  className="h-12 px-6 bg-gray-100 text-gray-700 font-bold rounded-xl border-2 border-gray-300 hover:bg-gray-200 transition-all"
                >
                  <DownloadSimple size={20} weight="bold" className="inline mr-2" />
                  Export All Data
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Plan Comparison Modal */}
      <PlanComparisonModal
        isOpen={showPlanComparison}
        onClose={() => setShowPlanComparison(false)}
        userType="contractor"
        currentPlan={currentPlan}
      />
    </div>
  );
};

export default ContractorControlPanel;
